import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatPrice, imageUrl, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Upload, X } from "lucide-react";

const empty = { name: "", brand: "", sku: "", category: "Jeans", gender: "Unisex", description: "", price: 0, previousPrice: null, sizes: [], colors: [], images: [], stock: 0, featured: false, isNew: false, onSale: false, active: true };

export default function AdminProducts() {
    const qc = useQueryClient();
    const [editing, setEditing] = useState(null);
    const { data: products = [] } = useQuery({ queryKey: ["admin-products"], queryFn: async () => (await api.get("/products/all-admin")).data });
    const { data: cats = [] } = useQuery({ queryKey: ["admin-cats"], queryFn: async () => (await api.get("/categories")).data });

    const save = async (form) => {
        try {
            const payload = { ...form, price: Number(form.price), previousPrice: form.previousPrice ? Number(form.previousPrice) : null, stock: Number(form.stock) };
            if (form.id) await api.put(`/products/${form.id}`, payload);
            else await api.post("/products", payload);
            toast.success("Producto guardado");
            qc.invalidateQueries({ queryKey: ["admin-products"] });
            qc.invalidateQueries({ queryKey: ["products"] });
            setEditing(null);
        } catch (e) { toast.error(apiErr(e)); }
    };
    const del = async (id) => {
        if (!window.confirm("¿Eliminar producto?")) return;
        await api.delete(`/products/${id}`);
        toast.success("Eliminado");
        qc.invalidateQueries({ queryKey: ["admin-products"] });
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <h1 className="font-display text-3xl">Productos</h1>
                <button onClick={() => setEditing(empty)} data-testid="btn-admin-create-product" className="btn-ink inline-flex items-center gap-2"><Plus className="w-4 h-4" />Nuevo producto</button>
            </div>
            <div className="bg-white border border-sandLight rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-bone">
                        <tr className="text-left text-xs uppercase tracking-wider text-ink/60">
                            <th className="p-3">Imagen</th><th>Nombre</th><th>Categoría</th><th>Precio</th><th>Stock</th><th>Estado</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((p) => (
                            <tr key={p.id} className="border-t border-sandLight">
                                <td className="p-3"><div className="w-12 aspect-square rounded overflow-hidden bg-sandLight">{p.images?.[0] && <img src={imageUrl(p.images[0])} className="w-full h-full object-cover" alt="" />}</div></td>
                                <td>{p.name}<br /><span className="text-xs text-ink/50">{p.brand}{p.sku ? ` · ITEM: ${p.sku}` : ""}</span></td>
                                <td>{p.category}</td>
                                <td>{formatPrice(p.price)}</td>
                                <td>{p.stock}</td>
                                <td><span className={`text-xs px-2 py-1 rounded-full ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-ink/10 text-ink/60"}`}>{p.active ? "Activo" : "Oculto"}</span></td>
                                <td className="p-3 text-right">
                                    <button onClick={() => setEditing(p)} data-testid={`btn-admin-edit-product-${p.id}`} className="text-ink hover:text-terracotta p-1"><Edit3 className="w-4 h-4" /></button>
                                    <button onClick={() => del(p.id)} data-testid={`btn-admin-delete-product-${p.id}`} className="text-ink hover:text-terracotta p-1"><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {editing && <ProductModal cats={cats} product={editing} onClose={() => setEditing(null)} onSave={save} />}
        </div>
    );
}

function ProductModal({ product, onClose, onSave, cats }) {
    const [f, setF] = useState({ ...product, sizes: product.sizes || [], colors: product.colors || [], images: product.images || [] });
    const [sizesText, setSizesText] = useState((product.sizes || []).join(", "));
    const [colorsText, setColorsText] = useState((product.colors || []).join(", "));
    const [sizeStock, setSizeStock] = useState({ ...(product.size_stock || {}) });
    const [colorStock, setColorStock] = useState({ ...(product.color_stock || {}) });
    const parseList = (t) => t.split(",").map((s) => s.trim()).filter(Boolean);
    const handleSave = () => {
        const sizes = parseList(sizesText);
        const colors = parseList(colorsText);
        const ss = {};
        sizes.forEach((s) => { ss[s] = Number(sizeStock[s]) || 0; });
        const cs = {};
        colors.forEach((c) => { cs[c] = Number(colorStock[c]) || 0; });
        const stock = sizes.length ? Object.values(ss).reduce((a, b) => a + b, 0)
            : colors.length ? Object.values(cs).reduce((a, b) => a + b, 0)
            : (Number(f.stock) || 0);
        onSave({ ...f, sizes, colors, size_stock: ss, color_stock: cs, stock });
    };
    const [uploading, setUploading] = useState(false);
    const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

    const uploadImg = async (e) => {
        const file = e.target.files?.[0]; if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            set("images", [...(f.images || []), data.url]);
            toast.success("Imagen subida");
        } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
    };

    const inp = "w-full border border-sandLight bg-white rounded-md px-3 py-2 text-sm outline-none focus:border-ink";
    return (
        <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-6 border-b border-sandLight">
                    <h2 className="font-display text-2xl">{f.id ? "Editar producto" : "Nuevo producto"}</h2>
                    <button onClick={onClose} className="text-ink/60"><X /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <input placeholder="Nombre" value={f.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="prod-name" />
                        <input placeholder="Marca" value={f.brand} onChange={(e) => set("brand", e.target.value)} className={inp} />
                        <input placeholder="ITEM (ej: JM-001)" value={f.sku || ""} onChange={(e) => set("sku", e.target.value)} className={inp} data-testid="prod-sku" />
                        <select value={f.category} onChange={(e) => set("category", e.target.value)} className={inp}>
                            {(cats.length ? cats.map((c) => c.name) : ["Jeans","Camisetas","Polos","Tops","Correas","Accesorios"]).map((c) => <option key={c}>{c}</option>)}
                        </select>
                        <select value={f.gender} onChange={(e) => set("gender", e.target.value)} className={inp}>
                            <option>Hombre</option><option>Mujer</option><option>Unisex</option>
                        </select>
                        <input type="text" inputMode="numeric" placeholder="Precio" value={f.price} onChange={(e) => set("price", e.target.value)} className={inp} data-testid="prod-price" />
                        <input type="text" inputMode="numeric" placeholder="Precio anterior (opcional)" value={f.previousPrice || ""} onChange={(e) => set("previousPrice", e.target.value)} className={inp} />
                        <input type="text" inputMode="numeric" placeholder="Stock (solo si no tiene tallas)" value={f.stock} onChange={(e) => set("stock", e.target.value)} className={inp} />
                        <input placeholder="Tallas separadas por coma (S, M, L)" value={sizesText} onChange={(e) => setSizesText(e.target.value)} className={inp} data-testid="prod-sizes" />
                        <input placeholder="Colores separados por coma" value={colorsText} onChange={(e) => setColorsText(e.target.value)} className={inp} />
                    </div>
                    {parseList(sizesText).length > 0 && (
                        <div>
                            <p className="text-eyebrow mb-2">Stock por talla</p>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                {parseList(sizesText).map((s) => (
                                    <label key={s} className="flex items-center gap-2 text-sm">
                                        <span className="notranslate font-medium w-10 shrink-0" translate="no">{s}</span>
                                        <input type="text" inputMode="numeric" placeholder="0" value={sizeStock[s] ?? ""} onChange={(e) => setSizeStock({ ...sizeStock, [s]: e.target.value.replace(/\D/g, "") })} className={inp} data-testid={`size-stock-${s}`} />
                                    </label>
                                ))}
                            </div>
                            <p className="text-[11px] text-ink/50 mt-1">El stock total se calcula solo sumando las tallas.</p>
                        </div>
                    )}
                    {parseList(colorsText).length > 0 && (
                        <div>
                            <p className="text-eyebrow mb-2">Stock por color</p>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                {parseList(colorsText).map((c) => (
                                    <label key={c} className="flex items-center gap-2 text-sm">
                                        <span className="notranslate font-medium w-14 shrink-0 truncate" translate="no">{c}</span>
                                        <input type="text" inputMode="numeric" placeholder="0" value={colorStock[c] ?? ""} onChange={(e) => setColorStock({ ...colorStock, [c]: e.target.value.replace(/\D/g, "") })} className={inp} data-testid={`color-stock-${c}`} />
                                    </label>
                                ))}
                            </div>
                            <p className="text-[11px] text-ink/50 mt-1">Si el producto no tiene tallas, el stock total sale de los colores.</p>
                        </div>
                    )}
                    <textarea placeholder="Descripción" value={f.description} onChange={(e) => set("description", e.target.value)} className={inp} rows={3} />

                    <div>
                        <p className="text-eyebrow mb-2">Imágenes</p>
                        <div className="flex flex-wrap gap-2 mb-2">
                            {f.images.map((img, i) => (
                                <div key={i} className="relative w-24 aspect-[4/5] rounded overflow-hidden border border-sandLight">
                                    <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
                                    <button onClick={() => set("images", f.images.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-ink text-bone rounded-full w-5 h-5 flex items-center justify-center text-xs">×</button>
                                </div>
                            ))}
                            <label className="w-24 aspect-[4/5] border-2 border-dashed border-sandLight rounded flex items-center justify-center cursor-pointer text-ink/50 hover:border-ink hover:text-ink">
                                <input type="file" accept="image/*" onChange={uploadImg} className="hidden" />
                                {uploading ? "..." : <Upload className="w-5 h-5" />}
                            </label>
                        </div>
                        <input placeholder="O pega URL de imagen y presiona Enter" onKeyDown={(e) => { if (e.key === "Enter" && e.target.value) { set("images", [...f.images, e.target.value]); e.target.value = ""; e.preventDefault(); } }} className={inp} />
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm">
                        <label className="flex items-center gap-2"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} />Destacado</label>
                        <label className="flex items-center gap-2"><input type="checkbox" checked={f.isNew} onChange={(e) => set("isNew", e.target.checked)} />Nuevo</label>
                        <label className="flex items-center gap-2"><input type="checkbox" checked={f.onSale} onChange={(e) => set("onSale", e.target.checked)} />Oferta</label>
                        <label className="flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} />Activo</label>
                    </div>
                </div>
                <div className="p-6 border-t border-sandLight flex justify-end gap-3">
                    <button onClick={onClose} className="btn-outline">Cancelar</button>
                    <button onClick={handleSave} data-testid="btn-save-product" className="btn-ink">Guardar</button>
                </div>
            </div>
        </div>
    );
}
